import axiosInstance from './axios-instance';
import { firstPositiveNumber, isMutationSuccess } from './cache-utils';
import { invalidateOrderDataCache, resolveClientId, resolveOrderId } from './cache-invalidation';

const getScopedAccountId = (accountId?: number): number => {
  if (typeof window === 'undefined') {
    return Number(accountId || 0);
  }
  const ownerAccountId = Number(localStorage.getItem('ownerAccountId') || '0');
  if (ownerAccountId > 0) {
    return ownerAccountId;
  }
  if (Number(accountId) > 0) {
    return Number(accountId);
  }
  return Number(localStorage.getItem('accountId') || '0');
};

const withScopedAccountId = (payload: any) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const scopedAccountId = getScopedAccountId(payload.accountId);
  if (!(scopedAccountId > 0)) {
    return payload;
  }

  return {
    ...payload,
    accountId: scopedAccountId,
  };
};

const normalizeHardwareMarkup = (value: any) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getAllInfo = async (draperyOrderId: number) => {
  try {
    const response = await axiosInstance.get(`/draperyOrder/getAllInfo?id=${draperyOrderId}`);

    return response.data;
  } catch (error) {
    console.error('Error getting all info for drapery order:', error);
    throw error;
  }
};
const addDraperyOrder = async (payload: any) => {
  try {
    const scopedPayload = withScopedAccountId(payload);
    let newPayload = {
      ...scopedPayload,
      hardwareQuantity:
        scopedPayload.hardwareQuantity === '' ? null : parseFloat(scopedPayload.hardwareQuantity),
      hardwareMarkup: normalizeHardwareMarkup(scopedPayload.hardwareMarkup),
      hardwareCustomerFacingPrice:
        scopedPayload.hardwareCustomerFacingPrice === ''
          ? null
          : parseFloat(scopedPayload.hardwareCustomerFacingPrice),
    };

    const response = await axiosInstance.post(`/draperyOrder/add`, newPayload);
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(
        firstPositiveNumber(response.data?.id, newPayload.id),
        resolveClientId(newPayload, response.data)
      );
    }

    return response.data;
  } catch (error) {
    console.error('Error adding new drapery order:', error);
    throw error;
  }
};
const editDraperyOrder = async (payload: any) => {
  try {
    const scopedPayload = withScopedAccountId(payload);
    let newPayload = {
      ...scopedPayload,
      hardwareQuantity:
        scopedPayload.hardwareQuantity === '' ? null : parseFloat(scopedPayload.hardwareQuantity),
      hardwareMarkup: normalizeHardwareMarkup(scopedPayload.hardwareMarkup),
      hardwareCustomerFacingPrice:
        scopedPayload.hardwareCustomerFacingPrice === ''
          ? null
          : parseFloat(scopedPayload.hardwareCustomerFacingPrice),
    };

    const response = await axiosInstance.put(`/draperyOrder/edit`, newPayload);
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(
        firstPositiveNumber(newPayload.id, response.data?.id),
        resolveClientId(newPayload, response.data)
      );
    }

    return response.data;
  } catch (error) {
    console.error('Error with edit drapery order:', error);
    throw error;
  }
};
const addDraperyOrderItem = async (payload: any) => {
  try {
    const response = await axiosInstance.post(`/draperyOrder/addItem`, withScopedAccountId(payload));
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(resolveOrderId(payload, response.data), resolveClientId(payload, response.data));
    }

    return response.data;
  } catch (error) {
    console.error('Error adding new drapery order item:', error);
    throw error;
  }
};

const updateOrderType = async (): Promise<any> => {
  try {
    const response = await axiosInstance.post(`/draperyOrder/update-order-type`, {});
    return response.data;
  } catch (error) {
    throw error;
  }
};
const getTotalNumberCount = async (payload: any) => {
  try {
    const scopedAccountId = getScopedAccountId(payload);
    const response = await axiosInstance.get(
      `/draperyOrder/get-total-number-count?accountId=${scopedAccountId}`
    );

    return response.data;
  } catch (error) {
    console.error('Error adding new drapery order item:', error);
    throw error;
  }
};
const userCanCreateNewOrder = async (payload: any) => {
  try {
    const scopedAccountId = getScopedAccountId(payload);
    const response = await axiosInstance.post(
      `/draperyOrder/user-can-create-new-order?accountId=${scopedAccountId}`
    );

    return response.data;
  } catch (error) {
    console.error('Error adding new drapery order item:', error);
    throw error;
  }
};

const getOrderingAndReceivingComponents = async (payload: any) => {
  try {
    const response = await axiosInstance.post(`/draperyOrder/ordering-and-receiving`, payload)
    return response.data
  } catch (error) {
    console.error('Error while fetching ordering and receiving data',error)
    throw error
  }
}

const getClientOrderingAndReceivingComponents = async (payload: any) => {
  try {
    const response = await axiosInstance.post(`/draperyOrder/client-ordering-and-receiving`, payload)
    return response.data
  } catch (error) {
    console.error('Error while fetching client ordering and receiving data', error)
    throw error
  }
}

const updateOrderingAndReceivingTable = async (payload: any) => {
  try {
    const response = await axiosInstance.post(`/draperyOrder/update-ordering-and-receiving`, payload)
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(resolveOrderId(payload, response.data), resolveClientId(payload, response.data))
    }
    return response.data
  } catch (error) {
    console.error(error)
    throw(error)
  }
}

const batchUpdateOrderingAndReceivingTable = async (payload: any) => {
  try {
    const response = await axiosInstance.post(`/draperyOrder/batch-update-ordering-and-receiving`, payload)
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(undefined, resolveClientId(payload, response.data))
    }
    return response.data
  } catch (error) {
    console.error(error)
    throw(error)
  }
}

const updateClientOrderingAndReceivingTable = async (payload: any) => {
  try {
    const response = await axiosInstance.post(`/draperyOrder/update-client-ordering-and-receiving`, payload)
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(resolveOrderId(payload, response.data), resolveClientId(payload, response.data))
    }
    return response.data
  } catch (error) {
    console.error(error)
    throw(error)
  }
}

export {
  getAllInfo,
  addDraperyOrder,
  addDraperyOrderItem,
  editDraperyOrder,
  updateOrderType,
  getTotalNumberCount,
  userCanCreateNewOrder,
  getOrderingAndReceivingComponents,
  getClientOrderingAndReceivingComponents,
  updateOrderingAndReceivingTable,
  batchUpdateOrderingAndReceivingTable,
  updateClientOrderingAndReceivingTable,
};
